// src/ExteriorPage.js
import React, { useState } from 'react';
import { dedupeChecklist } from './utils/dedupeChecklist';

export default function ExteriorPage({ checklist, images }) {
  const colorBlue  = '#153BC6';
  const lightGray1 = '#f7f7f7';
  const lightGray2 = '#eaeaea';
  const anchorSlug = 'exterior-damage';

  /* ------------------------------------------------------------ */
  /*  Collapse duplicates first                                   */
  /* ------------------------------------------------------------ */
  const uniqueRows = dedupeChecklist(checklist);

  // Split into wheel vs. non‑wheel AFTER deduping
  const wheelItems    = uniqueRows.filter((r) => /wheel|tire/i.test(r.DropdownValue));
  const nonWheelItems = uniqueRows.filter((r) => !/wheel|tire/i.test(r.DropdownValue));

  /* ------------------------------------------------------------ */
  /*  Scroll helper                                               */
  /* ------------------------------------------------------------ */
  const scrollToDamage = () => {
    const t = document.getElementById(`damage-${anchorSlug}`) ||
              document.getElementById('damage-report');
    if (t) t.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /* ------------------------------------------------------------ */
  /*  Image‑modal state                                           */
  /* ------------------------------------------------------------ */
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [zoomed,        setZoomed]        = useState(false);

  /* ------------------------------------------------------------ */
  /*  Helpers                                                     */
  /* ------------------------------------------------------------ */
  /* -------- render helpers (icon & images) --------------- */
  function renderDamageIcon(severity) {
    const baseStyle = {
      display: 'inline-block',
      verticalAlign: 'middle',
    };

    if (severity === 'Light') {
      // use the MagnifyingGlass.png from public/
      return (
        <img
          src="/MagnifyingGlass.png"
          alt="Inspect"
          style={{
            ...baseStyle,
            width: '1.4rem',
            height: '1.4rem',
          }}
        />
      );
    }

    if (severity === 'Moderate') {
      return <span style={{ ...baseStyle, color: 'orange', fontSize: '1.4rem' }}>&#9888;</span>;
    }
    if (severity === 'Severe') {
      return <span style={{ ...baseStyle, color: 'red', fontSize: '1.8rem' }}>&#10007;</span>;
    }
    return <span style={{ ...baseStyle, color: 'green', fontSize: '1.4rem' }}>&#10003;</span>;
  }

  /* ------------------------------------------------------------ */
  /*  Image URLs                                                  */
  /* ------------------------------------------------------------ */
  const exteriorImageKeys = [
    'LeftFrontCornerImage','LeftHeadLightImage','LeftFogLightImage','LeftFrontWheelImage',
    'LeftSideMirrorGlassImage','FullLeftSideImage','LeftRearWheelImage','LeftRearCornerImage',
    'RearImage','RoofImage','RightRearCornerImage','RightRearLightImage','RightRearWheelImage',
    'FullRightSideImage','RightSideMirrorGlassImage','RightFrontWheelImage','RightFrontHeadLightImage',
    'RightFrontFogLightImage','RightFrontCornerImage','FrontImage','GasCapImage',
  ];
  const exteriorUrls = exteriorImageKeys.map((k) => images[k]).filter(Boolean);

  const openModal =(i)=>{ setSelectedIndex(i); setZoomed(false); };
  const closeModal=()=>{ setSelectedIndex(null); setZoomed(false); };
  const toggleZoom=()=> setZoomed(z=>!z);
  const showPrev  =e=>{ e.stopPropagation(); if (selectedIndex>0)
                          { setSelectedIndex(i=>i-1); setZoomed(false);} };
  const showNext  =e=>{ e.stopPropagation(); if (selectedIndex<exteriorUrls.length-1)
                          { setSelectedIndex(i=>i+1); setZoomed(false);} };

  /* ------------------------------------------------------------ */
  /*  Render                                                      */
  /* ------------------------------------------------------------ */
  return (
    <div
      className="report-page"
      style={{
        width: '8.5in',
        minHeight: '11in',
        margin: '0 auto',
        padding: '0.3in',
        paddingBottom: '1in',     // extra bottom padding so logo doesn't overlap
        backgroundColor: '#fff',
        border: '1px solid #ccc',
        boxSizing: 'border-box',
        fontFamily: 'Arial, sans-serif',
        position: 'relative'
      }}
    >
      <h2 style={{ marginBottom: '0.2in', color: colorBlue }}>Exterior Inspection</h2>

      <div style={{ display: 'flex', gap: '10px' }}>
        {/* ---------------- Left: exterior checklist ---------------- */}
        <div style={{ flex: '0 0 50%' }}>
          <div style={{ borderLeft: `5px solid ${colorBlue}`, paddingLeft: 8 }}>
            <h3 style={{ color: colorBlue, marginBottom: 4, fontSize: '1rem' }}>
              Exterior Checklist
            </h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {nonWheelItems.map((item, idx) => {
                  const bg = idx % 2 === 0 ? lightGray1 : lightGray2;
                  const clickable = ['Light','Moderate','Severe'].includes(item.DamageSeverity);
                  return (
                    <tr
                      key={item.DropdownValueId ?? item.DropdownValue}
                      style={{ backgroundColor: bg }}
                    >
                      <td style={{ padding: '4px 8px' }}>{item.DropdownValue}</td>
                      <td
                        style={{
                          padding: '4px 8px',
                          textAlign: 'center',
                          cursor: clickable ? 'pointer' : 'default',
                        }}
                        onClick={clickable ? scrollToDamage : undefined}
                        title={clickable ? 'Jump to Damage Report' : undefined}
                      >
                        {renderDamageIcon(item.DamageSeverity)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* ---------------- Right: images + wheel checklist + notes ---------------- */}
        <div style={{ flex: '0 0 50%' }}>
          {/* Image grid */}
          <div
            style={{
              borderLeft: `5px solid ${colorBlue}`,
              paddingLeft: 8,
              marginBottom: '0.2in'
            }}
          >
            <h3 style={{ color: colorBlue, marginBottom: 4, fontSize: '1rem' }}>
              Exterior Images
            </h3>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                rowGap: '4px',
                columnGap: '8px',
                alignItems: 'flex-start',
                alignContent: 'flex-start',
              }}
            >
              {exteriorImageKeys.map((key) => {
                const url = images[key];
                if (!url) return null;
                const idx = exteriorUrls.indexOf(url);
                return (
                  <div
                    key={key}
                    style={{
                      width: '80px',
                      height: '80px',
                      border: '1px solid #ccc',
                      borderRadius: 4,
                      overflow: 'hidden',
                      cursor: 'pointer',
                    }}
                    onClick={() => openModal(idx)}
                  >
                    <img
                      src={url}
                      alt={key}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Wheel checklist */}
          {wheelItems.length > 0 && (
            <div style={{ borderLeft: `5px solid ${colorBlue}`, paddingLeft: 8, marginBottom: '0.2in' }}>
              <h3 style={{ color: colorBlue, marginBottom: 4, fontSize: '1rem' }}>
                Exterior Wheel Checklist
              </h3>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {wheelItems.map((item, idx) => {
                    const bg = idx % 2 === 0 ? lightGray1 : lightGray2;
                    const clickable = ['Light','Moderate','Severe'].includes(item.DamageSeverity);
                    return (
                      <tr
                        key={item.DropdownValueId ?? item.DropdownValue}
                        style={{ backgroundColor: bg }}
                      >
                        <td style={{ padding: '4px 8px' }}>{item.DropdownValue}</td>
                        <td
                          style={{
                            padding: '4px 8px',
                            textAlign: 'center',
                            cursor: clickable ? 'pointer' : 'default',
                          }}
                          onClick={clickable ? scrollToDamage : undefined}
                          title={clickable ? 'Jump to Damage Report' : undefined}
                        >
                          {renderDamageIcon(item.DamageSeverity)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Notes */}
          <div
            style={{
              borderLeft: `5px solid ${colorBlue}`,
              paddingLeft: 8,
              backgroundColor: '#f9f9f9',
              padding: '10px'
            }}
          >
            <h3 style={{ color: colorBlue, marginBottom: 6, fontSize: '1rem' }}>
              Important Exterior Notes
            </h3>
            <p style={{ margin: '6px 0' }}>
              We document all visible exterior damage so you know exactly what you're getting.
            </p>
            <p style={{ margin: '6px 0' }}>
              Some wear is normal for a used vehicle, like small chips, minor scratches, or light scuffs
              from daily driving.
            </p>
            <p style={{ margin: '6px 0' }}>
              We do our best to capture everything, but light wear like this is common and may be present
              even if not specifically noted in the report.
            </p>
          </div>
        </div>
      </div>

      {/* Logo */}
      <div style={{ position: 'absolute', bottom: 10, right: 10 }}>
        <img src="/logo.png" alt="Company Logo" style={{ height: 40 }} />
      </div>

      {/* Modal */}
      {selectedIndex !== null && exteriorUrls[selectedIndex] && (
        <div
          onClick={closeModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <img
            src={exteriorUrls[selectedIndex]}
            alt="Enlarged"
            onClick={(e) => {
              e.stopPropagation();
              toggleZoom();
            }}
            style={{
              maxWidth: zoomed ? '95%' : '50%',
              maxHeight: zoomed ? '95%' : '60%',
              border: '2px solid #fff',
              borderRadius: 6,
              cursor: zoomed ? 'zoom-out' : 'zoom-in',
              transition: 'all 0.3s ease',
            }}
          />

          {selectedIndex > 0 && (
            <div
              onClick={showPrev}
              style={{
                position: 'absolute',
                left: 20,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                fontSize: '3rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              &#10094;
            </div>
          )}

          {selectedIndex < exteriorUrls.length - 1 && (
            <div
              onClick={showNext}
              style={{
                position: 'absolute',
                right: 20,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                fontSize: '3rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              &#10095;
            </div>
          )}

          <div
            onClick={(e) => {
              e.stopPropagation();
              closeModal();
            }}
            style={{
              position: 'absolute',
              top: 10,
              right: 20,
              color: 'white',
              fontSize: '2rem',
              fontWeight: 'bold',
              cursor: 'pointer',
            }}
          >
            &times;
          </div>
        </div>
      )}
    </div>
  );
}
